/**
 * appProfiles.js — Two-level validator config hierarchy.
 *
 * Level 1 — App:              generic  (+ any custom/App-Pack apps)
 * Level 2 — Platform Profile: iosmob, droidmob, iostab, droidtab, all   (per app)
 *
 * App-level fields (shared across all platforms):
 *   adUnitPrefix, hasPrebid, hasAPS, hasAPSVideo, hasSpringServe, hasIMA, taxonomy
 *
 * Profile-level fields (platform-specific):
 *   publisherId, bundleId, allowedBannerSizes, prebidMappings, amazonSlotIds
 *
 * getActiveProfile() merges app + platform profile → same flat shape as before,
 * so all validators and downstream UI need no changes.
 *
 * Storage:
 *   adlensfx_active_app         = 'generic'
 *   adlensfx_active_app_profile = 'default'
 *   adlensfx_custom_apps        = JSON blob of custom apps
 *
 * Legacy key adlensfx_active_profile is migrated on first read.
 *
 * ── App Pack adapter note ──────────────────────────────────────────────────
 * BUILTIN_APPS is DERIVED from the App Pack store (src/api/appPacks.js). No
 * branded packs ship — the tool is app-agnostic — so out of the box only the
 * `generic` entry exists, synthesized in code (packs never define a generic
 * pack — see proxy-server/config/apps/README.md). Any custom/App-Pack app a
 * user adds is folded in via _buildBuiltinApps(); a pack with no legacy slug
 * alias (SLUG_TO_APP_ID) simply uses its slug as the app id.
 */

/* ─────────────────────────────────────────────────────────────────────────── */
/* Built-in Apps — derived from App Packs                                      */
/* ─────────────────────────────────────────────────────────────────────────── */

import { getPacks, subscribeAppPacks } from '../api/appPacks';

// Pack slug → legacy app id. This mapping is the only brand-specific literal
// left in this file — it exists solely to preserve existing localStorage keys
// (adlensfx_active_app values, BUILTIN_INITIALIZED entries) that predate the
// pack registry. Onboarding a new pack with no legacy id just falls through
// to using its slug as the app id directly (see _buildBuiltinApps below).
// Legacy slug→app-id aliases for packs whose id predates the pack registry.
// No built-in packs ship anymore (the tool is app-agnostic), so this is empty;
// a pack with no legacy alias just uses its slug as the app id directly (see
// _buildBuiltinApps below). Kept as an extension point for custom packs.
const SLUG_TO_APP_ID = {};

function _packProfileToAppProfile(profile) {
  if (!profile) return null;
  return {
    id:   profile.id,
    name: profile.name,
    publisherId:        profile.publisherId        ?? null,
    bundleId:           profile.bundleId           ?? null,
    allowedBannerSizes: profile.allowedBannerSizes ?? null,
    requiredCustParams: [],   // not populated by any current seed pack (see pack README §4)
    custParamRules:     [],   // app-level custParamRules carries the real ruleset
    expectedBidders:    null, // not populated by any current seed pack (see pack README §4)
    prebidMappings:     profile.prebidMappings ?? null,
    amazonSlotIds:      profile.amazonSlotIds  ?? null,
  };
}

function _packToBuiltinApp(pack) {
  const t = pack.taxonomy;
  const profiles = {};
  for (const [platformId, profile] of Object.entries(pack.stack?.profiles || {})) {
    profiles[platformId] = _packProfileToAppProfile(profile);
  }
  const brandCode = pack.identity?.brandCodes?.[0] || pack.slug;
  return {
    id:          SLUG_TO_APP_ID[pack.slug] || pack.slug,
    name:        pack.label || pack.slug,
    description: pack.description || '',
    adUnitPrefix:   pack.identity?.networkId ? `/${pack.identity.networkId}/${brandCode}/` : null,
    custParamRules: pack.custParamRules || [],
    hasPrebid:       pack.stack?.hasPrebid       ?? null,
    hasAPS:          pack.stack?.hasAPS          ?? null,
    hasAPSVideo:     pack.stack?.hasAPSVideo     ?? null,
    hasSpringServe:  pack.stack?.hasSpringServe  ?? null,
    hasIMA:          pack.stack?.hasIMA          ?? null,
    sslExcludedDomains: pack.sslExcludedDomains || [],
    taxonomy: t ? {
      networkId:     Number(t.networkId) || t.networkId,
      publishers:    t.publishers || [],
      intPublishers: t.interstitialPublisher ? [t.interstitialPublisher] : [],
      platforms:     t.platforms || [],
      flatPages:     t.pagesFlat || [],
      catPages:      t.pagesCategorised || [],
      bannerRange:   [1, t.maxBannerIndex ?? 25],
      specialUnits:  t.specialUnits || [],
    } : null,
    profiles,
  };
}

// Fallback generic app — packs never define a generic pack (by design, see
// proxy-server/config/apps/README.md §5), so it's still synthesized here.
const GENERIC_APP = {
  id:          'generic',
  name:        'Generic',
  description: 'Universal checks only — no client-specific expectations',
  adUnitPrefix:    null,
  hasPrebid:       null,
  hasAPS:          null,
  hasAPSVideo:     null,
  hasSpringServe:  null,
  hasIMA:          null,
  taxonomy:        null,
  profiles: {
    default: {
      id:   'default',
      name: 'Default',
      publisherId:        null,
      bundleId:           null,
      allowedBannerSizes: null,
      requiredCustParams: [],
      custParamRules:     [],
      expectedBidders:    null,
      prebidMappings:     null,
      amazonSlotIds:      null,
    },
  },
};

function _buildBuiltinApps(packs) {
  const apps = { generic: GENERIC_APP };
  for (const pack of Object.values(packs || {})) {
    if (!pack || pack.enabled === false) continue;
    apps[SLUG_TO_APP_ID[pack.slug] || pack.slug] = _packToBuiltinApp(pack);
  }
  return apps;
}

// Global STANDARD ruleset (ruleSchema.js shape). These are app-AGNOSTIC GAM /
// Prebid / privacy / viewability / header-bidding checks that a GAM-Inspector-class
// tool is expected to enforce on EVERY ad call, for every profile. They run
// automatically in runValidations() as the "Standard Checks" group and are NOT
// surfaced in per-app Config (which is only for app-specific cust_params).
//
// Why these live here and not only in the code-level validators: gampadChecks
// validates a param's *format* but skips absent params, so it never fails on a
// MISSING iu / sz / correlator. These presence/consent/OMID rules add that
// enforcement. Edit this list to change the standard baseline for all apps.
//
// Parameter paths follow readParam(): bare keys read the GAM URL query string,
// `cust_params.X` reads the decoded cust_params blob, dot-paths read the request body.
export const STANDARD_RULESET = [
  // ── GAM request structure (hard requirements — a malformed ad call fails) ──
  { id: 'fnc_gam_iu',        label: 'GAM ad unit (iu)',        builtin: true, enabled: true, requestTypes: ['google'], parameter: 'iu',         op: 'present',  pages: [], severity: 'fail', failMessage: 'GAM ad request missing iu (ad unit path)' },
  { id: 'fnc_gam_sz',        label: 'GAM creative sizes (sz)', builtin: true, enabled: true, requestTypes: ['google'], parameter: 'sz',         op: 'present',  pages: [], severity: 'fail', failMessage: 'GAM ad request missing sz (requested sizes)' },
  { id: 'fnc_gam_corr',      label: 'GAM correlator',          builtin: true, enabled: true, requestTypes: ['google'], parameter: 'correlator', op: 'present',  pages: [], severity: 'fail', failMessage: 'GAM ad request missing correlator (impression cannot be tied back)' },
  { id: 'fnc_gam_gdfpreq',   label: 'GAM request marker',      builtin: true, enabled: true, requestTypes: ['google'], parameter: 'gdfp_req',   op: 'equals', expectedValue: '1', pages: [], severity: 'fail', failMessage: 'gdfp_req must be 1 on a GAM ad request' },
  { id: 'fnc_gam_cust',      label: 'GAM cust_params present', builtin: true, enabled: true, requestTypes: ['google'], parameter: 'cust_params', op: 'present', pages: [], severity: 'warn', failMessage: 'No cust_params on GAM call — targeting/key-values not sent' },

  // ── Privacy & consent ──────────────────────────────────────────────────────
  { id: 'fnc_priv_npa',      label: 'Non-personalized ads flag (npa)', builtin: true, enabled: true, requestTypes: ['google'], parameter: 'npa',        op: 'oneOf', values: ['0', '1'], pages: [], severity: 'warn', failMessage: 'npa should be 0 or 1' },
  { id: 'fnc_priv_usp',      label: 'CCPA us_privacy string',          builtin: true, enabled: true, requestTypes: ['google'], parameter: 'us_privacy', op: 'matches', expectedPattern: '^1[YN-]{3}$', pages: [], severity: 'warn', failMessage: 'us_privacy not a valid IAB CCPA string (e.g. 1YNN / 1---)' },
  { id: 'fnc_priv_gpp',      label: 'GPP consent string',              builtin: true, enabled: true, requestTypes: ['google'], parameter: 'gpp',        op: 'present', pages: [], severity: 'info', failMessage: 'No GPP string on GAM call' },

  // ── Viewability / OMID ──────────────────────────────────────────────────────
  { id: 'fnc_omid_p',        label: 'OMID partner (omid_p)',   builtin: true, enabled: true, requestTypes: ['google'], parameter: 'omid_p', op: 'present', pages: [], severity: 'warn', failMessage: 'omid_p absent — Open Measurement viewability not signalled on this slot' },
  { id: 'fnc_omid_v',        label: 'OMID version (omid_v)',   builtin: true, enabled: true, requestTypes: ['google'], parameter: 'omid_v', op: 'present', pages: [], severity: 'info' },

  // ── Header-bidding lifecycle (informational — green when a bid won the slot) ──
  { id: 'fnc_hb_pb',         label: 'Prebid price bucket (hb_pb)', builtin: true, enabled: true, requestTypes: ['google'], parameter: 'cust_params.hb_pb',     op: 'matches', expectedPattern: '^\\d+\\.\\d{2}$', pages: [], severity: 'info', failMessage: 'hb_pb not a valid price bucket' },
  { id: 'fnc_hb_bidder',     label: 'Winning Prebid bidder',       builtin: true, enabled: true, requestTypes: ['google'], parameter: 'cust_params.hb_bidder', op: 'present', pages: [], severity: 'info' },
  { id: 'fnc_aps_amznb',     label: 'Amazon APS bid (amzn_b)',     builtin: true, enabled: true, requestTypes: ['google'], parameter: 'cust_params.amzn_b',    op: 'present', pages: [], severity: 'info' },

  // ── Prebid request integrity (on the /openrtb2 auction itself) ──────────────
  { id: 'fnc_prebid_imp',    label: 'Prebid request has impressions', builtin: true, enabled: true, requestTypes: ['prebid'], parameter: 'imp', op: 'present', pages: [], severity: 'warn', failMessage: 'Prebid auction body has no imp[] — nothing was put up for bid' },
];

// Mutable live binding — same "mutate in place" pattern as adUnitTaxonomy.js's
// APP_REGISTRY: reassigning BUILTIN_APPS itself wouldn't be visible to modules
// that imported the binding before a pack refresh, so subsequent updates clear
// + reassign the SAME object's keys instead. `import { BUILTIN_APPS } from
// '...'` call sites (ConfigPage.jsx, autoDetect.js) keep working unchanged.
export const BUILTIN_APPS = _buildBuiltinApps(getPacks());

/* ─────────────────────────────────────────────────────────────────────────── */
/* Backward-compat shim — old code that imports BUILTIN_PROFILES still works  */
/* ─────────────────────────────────────────────────────────────────────────── */

export const BUILTIN_PROFILES = {};
function _refreshBuiltinProfiles() {
  for (const k of Object.keys(BUILTIN_PROFILES)) delete BUILTIN_PROFILES[k];
  for (const app of Object.values(BUILTIN_APPS)) {
    BUILTIN_PROFILES[app.id] = { id: app.id, name: app.id === 'generic' ? 'Generic (Any App)' : app.name };
  }
}
_refreshBuiltinProfiles();

subscribeAppPacks((packs) => {
  const fresh = _buildBuiltinApps(packs);
  for (const k of Object.keys(BUILTIN_APPS)) delete BUILTIN_APPS[k];
  Object.assign(BUILTIN_APPS, fresh);
  _refreshBuiltinProfiles();
});

/* ─────────────────────────────────────────────────────────────────────────── */
/* Storage keys                                                                */
/* ─────────────────────────────────────────────────────────────────────────── */

const APP_KEY          = 'adlensfx_active_app';
const PLATFORM_KEY     = 'adlensfx_active_app_profile';
const CUSTOM_APPS_KEY  = 'adlensfx_custom_apps';
const INITIALIZED_KEY  = 'adlensfx_initialized';     // Set<'appId/profileId'>
const REMOVED_BUILTIN_KEY = 'adlensfx_removed_builtins'; // built-ins the user has hidden
const LEGACY_KEY       = 'adlensfx_active_profile';  // old flat key

// Profiles that ship pre-initialized (have real data, not stubs). The tool is
// app-agnostic now — no branded profiles ship, so nothing is pre-initialized.
const BUILTIN_INITIALIZED = new Set();

/* ─── DB sync helper (lazy import avoids circular deps) ─────────────────── */
// Called after every localStorage write so the backend DB stays in sync.
// Debounced inside profileSync — safe to call on every mutation.
function _syncToDb() {
  try {
    // Dynamic import so appProfiles.js itself has no import statements
    // (it's used in both browser and test environments).
    import('../utils/profileSync').then(m => m.push()).catch(() => {});
  } catch {}
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* Initialized profile registry                                                 */
/* ─────────────────────────────────────────────────────────────────────────── */

function _readInitialized() {
  // Built-in pre-initialized profiles are defined in CODE (BUILTIN_INITIALIZED),
  // so they are identical for every user and always present — no per-browser
  // seeding, no DB state needed for them. Custom-app initializations come from
  // localStorage (which is hydrated from the shared DB on boot via pullProfiles).
  // We union the two at read time, minus any the user has explicitly hidden.
  try {
    const stored  = new Set(JSON.parse(localStorage.getItem(INITIALIZED_KEY) || '[]'));
    const removed = new Set(JSON.parse(localStorage.getItem(REMOVED_BUILTIN_KEY) || '[]'));
    const merged  = new Set([...BUILTIN_INITIALIZED, ...stored]);
    for (const id of removed) merged.delete(id);
    return merged;
  } catch { return new Set(BUILTIN_INITIALIZED); }
}

function _writeInitialized(set) {
  try { localStorage.setItem(INITIALIZED_KEY, JSON.stringify([...set])); } catch {}
  _syncToDb();
}

/** True if this app+platform combo has been explicitly initialized by the user. */
export function isProfileInitialized(appId, profileId) {
  return _readInitialized().has(`${appId}/${profileId}`);
}

/**
 * Mark an app+platform as initialized (one-time setup).
 * Optionally pre-fill profile fields (publisherId, bundleId, etc.).
 */
export function initializeProfile(appId, profileId, fields = {}) {
  const set = _readInitialized();
  set.add(`${appId}/${profileId}`);
  _writeInitialized(set);
  if (Object.keys(fields).length) {
    updateProfileField(`${appId}/${profileId}`, fields);
  }
}

/** Remove the initialized flag (user deletes the profile from the UI). */
export function uninitializeProfile(appId, profileId) {
  const composite = `${appId}/${profileId}`;
  // Drop from the stored set...
  try {
    const stored = new Set(JSON.parse(localStorage.getItem(INITIALIZED_KEY) || '[]'));
    stored.delete(composite);
    localStorage.setItem(INITIALIZED_KEY, JSON.stringify([...stored]));
  } catch {}
  // ...and if it's a code-defined built-in, record it as hidden so the read-time
  // union doesn't resurrect it. This hidden set syncs to the DB like everything else.
  if (BUILTIN_INITIALIZED.has(composite)) {
    try {
      const removed = new Set(JSON.parse(localStorage.getItem(REMOVED_BUILTIN_KEY) || '[]'));
      removed.add(composite);
      localStorage.setItem(REMOVED_BUILTIN_KEY, JSON.stringify([...removed]));
    } catch {}
  }
  _syncToDb();
}

/**
 * Return only the profiles that have been initialized, as merged flat objects.
 * These are the cards shown in the Config UI.
 */
export function getInitializedProfiles() {
  const initialized = _readInitialized();
  const apps = getAllApps();
  const result = [];
  for (const app of Object.values(apps)) {
    for (const prof of Object.values(app.profiles || {})) {
      if (initialized.has(`${app.id}/${prof.id}`)) {
        result.push(mergeAppProfile(app, prof));
      }
    }
  }
  return result;
}

/**
 * Return platforms within an app that have NOT been initialized yet.
 * Used by the Initialize wizard to show only available options.
 */
export function getUninitializedPlatforms(appId) {
  const initialized = _readInitialized();
  const apps = getAllApps();
  const app = apps[appId];
  if (!app) return [];
  return Object.values(app.profiles || {}).filter(
    p => p.id !== 'all' && !initialized.has(`${appId}/${p.id}`)
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* Migration: flat profile → two-level                                         */
/* ─────────────────────────────────────────────────────────────────────────── */

let _migrated = false;
function migrateLegacy() {
  if (_migrated) return;
  _migrated = true;
  if (localStorage.getItem(APP_KEY)) return;   // already migrated
  const legacy = localStorage.getItem(LEGACY_KEY);
  if (!legacy) return;
  // Only 'generic' ships now; any legacy branded value falls through to generic.
  const map = {
    generic: ['generic', 'default'],
  };
  const [appId, profileId] = map[legacy] || ['generic', 'default'];
  localStorage.setItem(APP_KEY,      appId);
  localStorage.setItem(PLATFORM_KEY, profileId);
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* Merge app + platform profile → flat resolved object                         */
/* ─────────────────────────────────────────────────────────────────────────── */

function mergeAppProfile(app, platformProfile) {
  return {
    // Composite identity — unique key across (app, platform)
    id:          `${app.id}/${platformProfile.id}`,
    appId:       app.id,
    profileId:   platformProfile.id,
    name:        platformProfile.id === 'default'
                   ? app.name
                   : `${app.name} — ${platformProfile.name}`,
    appName:     app.name,
    profileName: platformProfile.name,
    description: app.description || '',

    // App-level fields (same for all platforms in this app)
    adUnitPrefix:   app.adUnitPrefix   ?? null,
    hasPrebid:      app.hasPrebid      ?? null,
    hasAPS:         app.hasAPS         ?? null,
    hasAPSVideo:    app.hasAPSVideo    ?? null,
    hasSpringServe: app.hasSpringServe ?? null,
    hasIMA:         app.hasIMA         ?? null,
    taxonomy:       platformProfile.taxonomy ?? app.taxonomy ?? null,

    // Platform-specific fields (profile overrides app)
    publisherId:        platformProfile.publisherId        ?? app.publisherId        ?? null,
    bundleId:           platformProfile.bundleId           ?? app.bundleId           ?? null,
    allowedBannerSizes: platformProfile.allowedBannerSizes ?? app.allowedBannerSizes ?? null,
    requiredCustParams: platformProfile.requiredCustParams ?? app.requiredCustParams  ?? [],
    custParamRules:     (platformProfile.custParamRules && platformProfile.custParamRules.length)
                          ? platformProfile.custParamRules
                          : (app.custParamRules ?? []),
    // Unified user-editable ruleset (ruleSchema.js shape). Authored in the Rules
    // editor; runs in runValidations() alongside the built-in checks.
    ruleSet:            (platformProfile.ruleSet && platformProfile.ruleSet.length)
                          ? platformProfile.ruleSet
                          : (app.ruleSet ?? []),
    expectedBidders:    platformProfile.expectedBidders    ?? app.expectedBidders     ?? null,
    prebidMappings:     platformProfile.prebidMappings     ?? app.prebidMappings      ?? null,
    amazonSlotIds:      platformProfile.amazonSlotIds      ?? app.amazonSlotIds       ?? null,
  };
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* App CRUD                                                                     */
/* ─────────────────────────────────────────────────────────────────────────── */

export function getAllApps() {
  const custom = (() => {
    try { return JSON.parse(localStorage.getItem(CUSTOM_APPS_KEY) || '{}'); }
    catch { return {}; }
  })();
  const merged = { ...BUILTIN_APPS, ...custom };
  // Dedupe display names case-insensitively ("Fox Sports" vs "Fox sports" vs a
  // second "Fox Sports" under another id): stale profiles accumulate in
  // localStorage/backend sync across renames, and every app selector renders
  // this map. Later entries win (custom overrides builtin), so users see one
  // entry per app name. The hidden duplicates still exist in storage — prune
  // them for good in Config → Apps.
  const byName = new Map();
  for (const [id, app] of Object.entries(merged)) {
    const key = String((app && app.name) || id).trim().toLowerCase();
    byName.set(key, [id, app]);
  }
  return Object.fromEntries(byName.values());
}

/** Returns { appId, profileId } for the currently active selection. */
export function getActiveSelection() {
  migrateLegacy();
  const appId = localStorage.getItem(APP_KEY) || 'generic';
  const stored = localStorage.getItem(PLATFORM_KEY);
  const apps = getAllApps();
  const app  = apps[appId] || BUILTIN_APPS.generic;
  const profiles = app.profiles || {};
  const profileId = (stored && profiles[stored])
    ? stored
    : Object.keys(profiles)[0] || 'default';
  return { appId, profileId };
}

/** Set both app and platform profile at once. */
export function setActiveSelection(appId, profileId) {
  localStorage.setItem(APP_KEY,      appId);
  localStorage.setItem(PLATFORM_KEY, profileId);
}

/**
 * Returns the resolved (merged) active profile.
 * Shape is identical to the old flat profile — all downstream code works unchanged.
 *
 * If no profiles have been initialized yet, falls back to a "Standard" generic
 * profile — universal structural checks only, no client-specific rules.
 * This lets the tool work out-of-the-box for any ad traffic without configuration.
 */
export function getActiveProfile() {
  // If nothing has been initialized, return a clean generic fallback
  // so the tool still validates structural IAB/GAM rules on any traffic.
  const initialized = _readInitialized();
  const { appId, profileId } = getActiveSelection();

  // If the current selection isn't initialized, fall back to generic
  const selectionKey = `${appId}/${profileId}`;
  const useGeneric = initialized.size === 0 || (!initialized.has(selectionKey) && appId !== 'generic');

  const resolvedAppId     = useGeneric ? 'generic'  : appId;
  const resolvedProfileId = useGeneric ? 'default'  : profileId;

  const apps = getAllApps();
  const app  = apps[resolvedAppId] || BUILTIN_APPS.generic;
  const profiles = app.profiles || {};
  const platformProfile = profiles[resolvedProfileId] || Object.values(profiles)[0] || { id: 'default', name: 'Default' };

  const merged = mergeAppProfile(app, platformProfile);

  // Mark clearly as generic fallback so the UI can show it differently
  if (useGeneric) {
    merged._isFallback = true;
    merged.name        = 'Standard (no profile active)';
  }

  return merged;
}

/**
 * Backward compat: treat the id as an app id and switch to its first platform profile.
 * New code should use setActiveSelection(appId, profileId) instead.
 */
export function setActiveProfileId(id) {
  const apps = getAllApps();
  if (apps[id]) {
    const firstProfile = Object.keys(apps[id].profiles || {})[0] || 'default';
    setActiveSelection(id, firstProfile);
  }
}

/**
 * Backward compat: returns a flat map of all merged (app/profile) combos.
 * Key is the composite id 'appId/profileId' (or 'generic' for the generic default).
 */
export function getAllProfiles() {
  const apps = getAllApps();
  const out = {};
  for (const app of Object.values(apps)) {
    for (const prof of Object.values(app.profiles || {})) {
      const key = app.id === 'generic' && prof.id === 'default'
        ? 'generic'
        : `${app.id}/${prof.id}`;
      out[key] = mergeAppProfile(app, prof);
    }
  }
  return out;
}

export function saveCustomApp(app) {
  if (BUILTIN_APPS[app.id]) throw new Error(`Cannot overwrite built-in app "${app.id}"`);
  const custom = (() => {
    try { return JSON.parse(localStorage.getItem(CUSTOM_APPS_KEY) || '{}'); }
    catch { return {}; }
  })();
  custom[app.id] = app;
  localStorage.setItem(CUSTOM_APPS_KEY, JSON.stringify(custom));
  _syncToDb();
}

export function deleteCustomApp(id) {
  if (BUILTIN_APPS[id]) return;
  const custom = (() => {
    try { return JSON.parse(localStorage.getItem(CUSTOM_APPS_KEY) || '{}'); }
    catch { return {}; }
  })();
  delete custom[id];
  localStorage.setItem(CUSTOM_APPS_KEY, JSON.stringify(custom));
  _syncToDb();
}

/**
 * Update fields on a specific app+profile combo.
 *
 * profileId can be:
 *   'generic/iosmob'  — new composite form (app/platform)
 *   'generic'         — legacy form: updates first platform profile of that app
 *
 * Fields that belong to the platform profile (publisherId, bundleId,
 * allowedBannerSizes, prebidMappings, amazonSlotIds, requiredCustParams,
 * expectedBidders, taxonomy) go into the platform profile object.
 * App-level fields (adUnitPrefix, hasPrebid, …) go into the app object.
 */
export function updateProfileField(profileId, fields) {
  let appId, profId;
  if (profileId && profileId.includes('/')) {
    [appId, profId] = profileId.split('/');
  } else {
    appId  = profileId || 'generic';
    const apps = getAllApps();
    profId = Object.keys(apps[appId]?.profiles || {})[0] || 'default';
  }

  const all = getAllApps();
  // Deep-clone so we don't mutate the builtin reference
  const app = JSON.parse(JSON.stringify(all[appId] || { id: appId, name: appId, profiles: {} }));
  app.profiles = app.profiles || {};
  const existingProfile = app.profiles[profId] || { id: profId, name: profId };

  // Split fields between profile-level and app-level
  const PROFILE_FIELDS = new Set([
    'publisherId', 'bundleId', 'allowedBannerSizes',
    'prebidMappings', 'amazonSlotIds', 'requiredCustParams', 'custParamRules',
    'ruleSet', 'expectedBidders', 'taxonomy',
  ]);
  const APP_FIELDS = new Set([
    'adUnitPrefix', 'hasPrebid', 'hasAPS', 'hasAPSVideo',
    'hasSpringServe', 'hasIMA', 'name', 'description', 'sslExcludedDomains',
  ]);

  const profileUpdate = {};
  const appUpdate = {};
  for (const [k, v] of Object.entries(fields)) {
    if (PROFILE_FIELDS.has(k)) profileUpdate[k] = v;
    else if (APP_FIELDS.has(k)) appUpdate[k] = v;
    else profileUpdate[k] = v;  // unknown: goes to profile
  }

  app.profiles[profId] = { ...existingProfile, ...profileUpdate, id: profId };
  Object.assign(app, appUpdate);

  const custom = (() => {
    try { return JSON.parse(localStorage.getItem(CUSTOM_APPS_KEY) || '{}'); }
    catch { return {}; }
  })();
  custom[appId] = app;
  localStorage.setItem(CUSTOM_APPS_KEY, JSON.stringify(custom));
}

/** Backward compat: save a flat custom profile as a single-profile custom app. */
export function saveCustomProfile(profile) {
  const appId = profile.id;
  if (BUILTIN_APPS[appId]) throw new Error(`Cannot overwrite built-in app "${appId}"`);
  const app = {
    id:          appId,
    name:        profile.name        || appId,
    description: profile.description || '',
    adUnitPrefix:   profile.adUnitPrefix   ?? null,
    hasPrebid:      profile.hasPrebid      ?? null,
    hasAPS:         profile.hasAPS         ?? null,
    hasAPSVideo:    profile.hasAPSVideo    ?? null,
    hasSpringServe: profile.hasSpringServe ?? null,
    hasIMA:         profile.hasIMA         ?? null,
    taxonomy:       profile.taxonomy       ?? null,
    profiles: {
      default: {
        id:   'default',
        name: 'Default',
        publisherId:        profile.publisherId        ?? null,
        bundleId:           profile.bundleId           ?? null,
        allowedBannerSizes: profile.allowedBannerSizes ?? null,
        requiredCustParams: profile.requiredCustParams ?? [],
        custParamRules:     profile.custParamRules     ?? [],
        expectedBidders:    profile.expectedBidders    ?? null,
        prebidMappings:     profile.prebidMappings     ?? null,
        amazonSlotIds:      profile.amazonSlotIds      ?? null,
      },
    },
  };
  saveCustomApp(app);
}

/** Backward compat: delete a custom app by its old profile id. */
export function deleteCustomProfile(id) {
  deleteCustomApp(id);
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* Prebid stored request ID lookup                                              */
/* ─────────────────────────────────────────────────────────────────────────── */

/**
 * Derive the Prebid stored request ID for a GAM ad unit path.
 * Key format: brand_platform_pageType_pos  (all lowercase)
 * IU format:  /network/brand/platform/pageType/section/pos
 */
export function lookupPrebidStoredId(iu, mappings) {
  if (!iu || !mappings) return null;
  const parts = iu.split('/').filter(Boolean);
  if (parts.length < 5) return null;
  const brand    = parts[1];
  const platform = parts[2];
  const pageType = parts[3];
  const pos      = parts[parts.length - 1];
  const exactKey = `${brand}_${platform}_${pageType}_${pos}`.toLowerCase();
  if (mappings[exactKey]) return mappings[exactKey];
  if (pos.startsWith('ban')) {
    const banKey = `${brand}_${platform}_${pageType}_banner`.toLowerCase();
    if (mappings[banKey]) return mappings[banKey];
  }
  return null;
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* Amazon APS / Taxonomy helpers                                                */
/* ─────────────────────────────────────────────────────────────────────────── */

/** Return the active profile's amazon slot IDs map, or null if not configured. */
export function getProfileAmazonSlots() {
  return getActiveProfile().amazonSlotIds || null;
}

/** Return the active profile's taxonomy, or null to use the global default. */
export function getProfileTaxonomy() {
  return getActiveProfile().taxonomy || null;
}

/* ─────────────────────────────────────────────────────────────────────────── */
/* Per-app SSL Exclusions                                                       */
/* ─────────────────────────────────────────────────────────────────────────── */

/** Return the SSL-excluded domains for a given app (built-in defaults + any saved overrides). */
export function getAppSslExclusions(appId) {
  const apps = getAllApps();
  return apps[appId]?.sslExcludedDomains || [];
}

/** Save an updated SSL exclusion list for an app (persisted to custom apps in localStorage). */
export function setAppSslExclusions(appId, domains) {
  const all = getAllApps();
  const app = JSON.parse(JSON.stringify(all[appId] || { id: appId, name: appId, profiles: {} }));
  app.sslExcludedDomains = domains;
  const custom = (() => {
    try { return JSON.parse(localStorage.getItem(CUSTOM_APPS_KEY) || '{}'); }
    catch { return {}; }
  })();
  custom[appId] = app;
  localStorage.setItem(CUSTOM_APPS_KEY, JSON.stringify(custom));
}
